import { useState } from 'react';

import { ProjectSelect } from '@/components/project-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { VotingMode } from '@/lib/voting';

export interface VotingPeriodValues {
  projectId: string;
  project: string;
  mode: VotingMode;
  opensAt: string;
  closesAt: string;
}

interface VotingPeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'extend';
  initialValues?: Partial<VotingPeriodValues>;
  onSubmit: (values: VotingPeriodValues) => void;
}

export function VotingPeriodDialog({
  open,
  onOpenChange,
  mode: dialogMode,
  initialValues,
  onSubmit,
}: VotingPeriodDialogProps) {
  const [projectId, setProjectId] = useState(initialValues?.projectId ?? '');
  const [project, setProject] = useState(initialValues?.project ?? '');
  const [mode, setMode] = useState<VotingMode>(initialValues?.mode ?? 'scheduled');
  const [opensAt, setOpensAt] = useState(initialValues?.opensAt ?? '');
  const [closesAt, setClosesAt] = useState(initialValues?.closesAt ?? '');
  const [showErrors, setShowErrors] = useState(false);

  const isExtend = dialogMode === 'extend';
  const isManual = mode === 'manual';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isExtend && !projectId) {
              setShowErrors(true);
              return;
            }
            onSubmit({ projectId, project, mode, opensAt, closesAt });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>{isExtend ? 'Extend voting period' : 'New voting period'}</DialogTitle>
            <DialogDescription>
              {isExtend
                ? 'Push the closing date back to extend voting.'
                : 'Set when voting opens and closes for a project.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="voting-project">Project</Label>
            {isExtend ? (
              <Input id="voting-project" value={project} readOnly />
            ) : (
              <ProjectSelect
                value={project}
                onChange={(option) => {
                  setProjectId(option.id);
                  setProject(option.name);
                }}
                placeholder="Select a project"
              />
            )}
            {showErrors && !isExtend && !projectId && (
              <p className="text-destructive text-xs">Select a project.</p>
            )}
          </div>

          {!isExtend && (
            <div className="grid gap-2">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={(value) => setMode(value as VotingMode)}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled (closes on a date)</SelectItem>
                  <SelectItem value="manual">Manual (close by hand)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="voting-opens">Opens</Label>
              <Input
                id="voting-opens"
                type="date"
                value={opensAt}
                onChange={(event) => setOpensAt(event.target.value)}
                disabled={isExtend}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="voting-closes">Closes</Label>
              <Input
                id="voting-closes"
                type="date"
                value={closesAt}
                onChange={(event) => setClosesAt(event.target.value)}
                disabled={isExtend || isManual}
                required={!isManual}
              />
              {isManual && (
                <p className="text-muted-foreground text-xs">Open until closed manually.</p>
              )}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">{isExtend ? 'Extend period' : 'Create period'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
